// frontend/src/components/tickets/TicketQuickActions.jsx
//
// Barra de acciones + análisis para el POPUP rápido del ticket (el que se abre
// desde el sidebar del vehículo).
//
// Objetivo: que desde el popup se pueda decidir y actuar sin tener que abrir la
// página completa. El análisis se pide bajo demanda y NUNCA bloquea el diálogo:
// si la IA no está disponible, el backend responde igualmente con el análisis
// basado en las reglas del sistema.

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  AddTask as TaskIcon,
  Forum as ChatIcon,
  WhatsApp as WhatsAppIcon,
  DirectionsCar as VehicleIcon,
  Tune as ProfileIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { api } from '../../services/api';

const TicketQuickActions = ({ ticket, onClose }) => {
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const ticketId = ticket?.id || ticket?.ticket_id;

  const runAnalysis = () => {
    if (!ticketId || loading) return;
    setLoading(true);
    setError(null);
    api.alarmTickets.ticketAiAnalysis(ticketId)
      .then((res) => setAnalysis(res.data))
      .catch(() => setError('No se pudo generar el análisis en este momento.'))
      .finally(() => setLoading(false));
  };

  // Las acciones que necesitan diálogo propio (tarea, chat, WhatsApp) viven en
  // la página completa: se navega allí y se dispara la acción al llegar, en vez
  // de duplicar esos formularios dentro del popup.
  const goToTicket = (action = null) => {
    if (!ticketId) return;
    if (onClose) onClose();
    navigate(`/alarm-tickets/${ticketId}${action ? `?action=${action}` : ''}`);
  };

  const goToVehicle = () => {
    if (onClose) onClose();
    navigate('/vehicles/schematics');
  };

  const goToProfile = () => {
    if (onClose) onClose();
    navigate(ticket?.profile_id ? `/alarm-profiles?profile=${ticket.profile_id}` : '/alarm-profiles');
  };

  return (
    <Box>
      <Divider sx={{ mb: 1.5 }} />
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Acciones
      </Typography>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button size="small" variant="contained" startIcon={loading ? <CircularProgress size={14} /> : <AIIcon />}
          onClick={runAnalysis} disabled={loading}>
          Analizar con IA
        </Button>
        <Button size="small" variant="outlined" startIcon={<TaskIcon />} onClick={() => goToTicket('task')}>
          Crear tarea
        </Button>
        <Button size="small" variant="outlined" startIcon={<ChatIcon />} onClick={() => goToTicket('chat')}>
          Chat
        </Button>
        <Button size="small" variant="outlined" startIcon={<WhatsAppIcon />} onClick={() => goToTicket('whatsapp')}>
          WhatsApp
        </Button>
        <Button size="small" variant="text" startIcon={<VehicleIcon />} onClick={goToVehicle}>
          Ver vehículo
        </Button>
        <Button size="small" variant="text" startIcon={<ProfileIcon />} onClick={goToProfile}>
          Perfil de alertas
        </Button>
        <Button size="small" variant="text" startIcon={<OpenIcon />} onClick={() => goToTicket()}>
          Página completa
        </Button>
      </Stack>

      {error && <Alert severity="warning" sx={{ mt: 1.5 }}>{error}</Alert>}

      {analysis && (
        <Box sx={{
          mt: 1.5, p: 1.5, borderRadius: 1,
          border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover',
        }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <AIIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>
              Análisis del ticket
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={analysis.generated_by === 'rules' ? 'Reglas del sistema' : 'IA'}
              sx={{ height: 20, fontSize: '0.68rem' }}
            />
          </Stack>

          <Stack spacing={0.75}>
            {analysis.summary && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>QUÉ PASÓ</Typography>
                <Typography variant="body2">{analysis.summary}</Typography>
              </Box>
            )}
            {(analysis.causes || []).length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>POSIBLE CAUSA</Typography>
                <Typography variant="body2">{analysis.causes.join(' · ')}</Typography>
              </Box>
            )}
            {analysis.risk && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>RIESGO</Typography>
                <Typography variant="body2">{analysis.risk}</Typography>
              </Box>
            )}
            {analysis.action && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>ACCIÓN RECOMENDADA</Typography>
                <Typography variant="body2" fontWeight={600} color="primary.main">{analysis.action}</Typography>
              </Box>
            )}
            {analysis.recurrence_note && (
              <Alert severity="info" sx={{ py: 0.25 }}>
                <Typography variant="caption">{analysis.recurrence_note}</Typography>
              </Alert>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

TicketQuickActions.propTypes = {
  ticket: PropTypes.object,
  onClose: PropTypes.func,
};

export default TicketQuickActions;
